import { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "devextreme-react/button";
import { DataGrid } from "devextreme-react/data-grid";
import {
  Column,
  ColumnChooser,
  FilterRow,
  Paging,
  SearchPanel,
  Selection,
  Sorting,
} from "devextreme-react/data-grid";
import { LoadPanel } from "devextreme-react/load-panel";
import { Popup } from "devextreme-react/popup";
import { TextBox } from "devextreme-react/text-box";

import { MainCard } from "@ezhou/app_mantis_1_1";
import { PageProps, UpdateDomTitleForForm } from "@ezhou/core";

import {
  getUserProfileFromCache,
  NoExistingOrganizationId,
} from "../../services/userSettingService";
import { GetAccountingRulesRequest } from "./model/model";

type MappingConfigVersionApiModel = {
  organizationId: number;
  typeName?: string;
  versionName?: string;
  createdBy?: string;
  createdTime?: string;
};

type MappingConfigVersionResponse = {
  versions?: MappingConfigVersionApiModel[];
};

type MappingConfigVersionRow = {
  rowId: string;
  organizationId: number;
  typeName?: string;
  versionName?: string;
  createdBy?: string;
  createdTime?: Date;
};

export const MappingConfigVersions = (pageProps: PageProps) => {
  useEffect(() => {
    UpdateDomTitleForForm(pageProps);
  }, [pageProps]);

  const logger = pageProps.loggerProvider.getLogger("MappingConfigVersions");
  const organizationId = useMemo(
    () => getUserProfileFromCache(pageProps.dataStore).defaultOrganization,
    [pageProps.dataStore],
  );

  const [versions, setVersions] = useState<MappingConfigVersionRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string>();
  const [isRestoring, setIsRestoring] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<MappingConfigVersionRow>();
  const [isSaveDialogVisible, setIsSaveDialogVisible] = useState(false);
  const [newVersionName, setNewVersionName] = useState("");

  const isBusy =
    isLoading || isRestoring || isDownloading || isSaving || isImporting;

  const httpClient = pageProps.appUtil.httpClients["eAccountingService"];

  // Reads the most recent error notification (appended by the app's default http
  // error handler) so the server's exception details can be surfaced to the user.
  const buildServerErrorMessage = useCallback(
    (notificationsBefore: number, fallbackMessage: string): string => {
      const notifications = pageProps.appUtil.getApp().getNotifications();
      const latest =
        notifications.length > notificationsBefore
          ? notifications[notifications.length - 1]
          : undefined;
      if (latest && latest.type === "error") {
        const detail = latest.message || latest.detailMessage;
        if (detail) {
          return `${detail}\n\nPlease check the error details in notification center.`;
        }
      }
      return fallbackMessage;
    },
    [pageProps.appUtil],
  );

  const loadVersions = useCallback(async () => {
    if (organizationId === NoExistingOrganizationId) {
      setIsLoading(false);
      setVersions([]);
      setSelectedVersion(undefined);
      setLoadError("No organization is currently selected.");
      return;
    }

    setIsLoading(true);
    setLoadError(undefined);
    setSelectedVersion(undefined);
    logger.debug(
      `Loading mapping configuration versions for organization ${organizationId}`,
    );

    try {
      const response = await httpClient.get<MappingConfigVersionResponse>(
        `amr-management/get-available-accounting-rules-versions/${organizationId}`,
        (err) => {
          throw err;
        },
      );

      const normalizedVersions = (response?.versions ?? []).map((version) => ({
        rowId: `${version.typeName ?? "unknown"}-${version.versionName ?? "n/a"}-${
          version.createdTime ?? ""
        }`,
        organizationId: version.organizationId,
        typeName: version.typeName,
        versionName: version.versionName,
        createdBy: version.createdBy,
        createdTime: version.createdTime ? new Date(version.createdTime) : undefined,
      }));

      normalizedVersions.sort((a, b) => {
        const aTime = a.createdTime ? a.createdTime.getTime() : 0;
        const bTime = b.createdTime ? b.createdTime.getTime() : 0;
        return bTime - aTime;
      });

      setVersions(normalizedVersions);
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === "string"
          ? error
          : "Unknown error";
      logger.error(`Failed to load mapping configuration versions: ${message}`);
      setVersions([]);
      setLoadError("Unable to load snapshot versions. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [httpClient, logger, organizationId]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  const openSaveDialog = useCallback(() => {
    if (organizationId === NoExistingOrganizationId) {
      alert("No organization is currently selected.");
      return;
    }

    setNewVersionName("");
    setIsSaveDialogVisible(true);
  }, [organizationId]);

  const saveCurrentRules = useCallback(async () => {
    const trimmedVersionName = newVersionName.trim();
    if (!trimmedVersionName) {
      alert("Snapshot version name cannot be empty.");
      return;
    }

    setIsSaveDialogVisible(false);
    setIsSaving(true);
    logger.info(
      `Saving current mapping configuration as version '${trimmedVersionName}' for organization ${organizationId}`,
    );

    try {
      await httpClient.post(
        "amr-management/save-accounting-rules",
        {
          versionName: trimmedVersionName,
          organizationId,
        },
        (err) => {
          throw err;
        },
      );
      alert(`Version '${trimmedVersionName}' saved successfully.`);
      await loadVersions();
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === "string"
          ? error
          : "Unknown error";
      logger.error(`Failed to save mapping configuration version: ${message}`);
      alert("Failed to save the current configuration. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [httpClient, loadVersions, logger, newVersionName, organizationId]);

  const restoreSelectedVersion = useCallback(async () => {
    if (!selectedVersion || !selectedVersion.versionName) {
      alert("Please select a snapshot version to restore.");
      return;
    }

    if(confirm(`Are you sure you want to restore the version ${selectedVersion.versionName}? This will overwrite the current mapping configuration.`) === false) {
      return;
    }

    setIsRestoring(true);
    logger.info(
      `Restoring mapping configuration version '${selectedVersion.versionName}' for organization ${organizationId}`,
    );

    const notificationsBefore = pageProps.appUtil.getApp().getNotifications().length;
    try {
      // Do not pass a custom error handler here. The app's default http error
      // handler appends an error notification containing the server's exception
      // details; overriding it would swallow that notification.
      const response = await httpClient.post(
        "amr-management/restore-accounting-rules",
        {
          versionName: selectedVersion.versionName,
          organizationId,
        },
      );

      if (response) {
        alert(`Version '${selectedVersion.versionName}' restored successfully.`);
        await loadVersions();
      } else {
        logger.error(
          `Failed to restore mapping configuration version '${selectedVersion.versionName}'.`,
        );
        alert(
          buildServerErrorMessage(
            notificationsBefore,
            "Failed to restore the selected version. Please try again.",
          ),
        );
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === "string"
          ? error
          : "Unknown error";
      logger.error(`Failed to restore mapping configuration version: ${message}`);
      alert(
        buildServerErrorMessage(
          notificationsBefore,
          "Failed to restore the selected version. Please try again.",
        ),
      );
    } finally {
      setIsRestoring(false);
    }
  }, [buildServerErrorMessage, httpClient, loadVersions, logger, organizationId, pageProps.appUtil, selectedVersion]);

  const downloadRules = useCallback(async (isCurrent: boolean) => {
    const request : GetAccountingRulesRequest = {
      organizationId: organizationId,
    };
    
    if(!isCurrent){
      if (!selectedVersion || !selectedVersion.versionName) {
        alert("Please select a snapshot version to download.");
        return;
      }
      request.versionName = selectedVersion.versionName;
    }

    setIsDownloading(true);

    try {
      const response = await httpClient.post(
        "amr-management/get-accounting-rules",
        request,
        (err) => {
          throw err;
        },
      );

      const jsonPayload = JSON.stringify(response, null, 2);
      const blob = new Blob([jsonPayload], { type: "application/json" });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeVersionName = selectedVersion?.versionName?.replace(/[^a-zA-Z0-9-_]/g, "_");
      link.href = downloadUrl;
      link.download = `${safeVersionName || "accounting-rules"}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === "string"
          ? error
          : "Unknown error";
      logger.error(`Failed to download mapping configuration version: ${message}`);
      alert("Failed to download the selected version. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  }, [organizationId, selectedVersion]);

  const importRules = useCallback(() => {
    if (organizationId === NoExistingOrganizationId) {
      alert("No organization is currently selected.");
      return;
    }

    const inputElement = document.createElement("input");
    inputElement.type = "file";
    inputElement.accept = "application/json";

    inputElement.onchange = async () => {
      const file = inputElement.files?.[0];
      if (!file) {
        return;
      }

      try {
        setIsImporting(true);
        const fileContent = await file.text();
        const accountingRules = JSON.parse(fileContent);

        await httpClient.post(
          "amr-management/import-accounting-rules",
          {
            organizationId,
            accountingRules,
          },
          (err) => {
            throw err;
          },
        );

        alert("Accounting rules imported successfully.");
        await loadVersions();
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? error.message
            : typeof error === "string"
            ? error
            : "Unknown error";
        logger.error(`Failed to import mapping configuration: ${message}`);
        alert("Failed to import accounting rules. Please verify the file and try again.");
      } finally {
        setIsImporting(false);
      }
    };

    inputElement.click();
  }, [httpClient, loadVersions, logger, organizationId]);

  return (
    <MainCard title="Mapping Configuration Snapshots">
      <div
        style={{
          display: "flex",
          gap: "12px",
          alignItems: "center",
          marginBottom: "12px",
          flexWrap: "wrap",
        }}
      >
        <Button
          icon="refresh"
          text="Refresh"
          stylingMode="contained"
          onClick={loadVersions}
          disabled={isLoading || organizationId === NoExistingOrganizationId}
        />
        <Button
          icon="undo"
          text="Restore Selected"
          stylingMode="contained"
          type="default"
          onClick={restoreSelectedVersion}
          disabled={
            isRestoring ||
            isLoading ||
            organizationId === NoExistingOrganizationId ||
            !selectedVersion?.versionName
          }
        />
        <Button
          icon="download"
          text="Download Selected"
          stylingMode="contained"
          type="default"
          onClick={() => downloadRules(false)}
          disabled={
            isDownloading ||
            isLoading ||
            organizationId === NoExistingOrganizationId ||
            !selectedVersion?.versionName
          }
        />
        <Button
          icon="import"
          text="Import"
          stylingMode="contained"
          type="default"
          onClick={() => importRules()}
          disabled={
            isImporting ||
            isDownloading ||
            isLoading ||
            organizationId === NoExistingOrganizationId
          }
        />
        <Button
          icon="download"
          text="Download Current"
          stylingMode="contained"
          type="default"
          onClick={() => downloadRules(true)}
          disabled={
            isDownloading ||
            isLoading ||
            organizationId === NoExistingOrganizationId
          }
        />
        <Button
          icon="save"
          text="Save Current"
          stylingMode="contained"
          type="default"
          onClick={openSaveDialog}
          disabled={
            isSaving ||
            isLoading ||
            organizationId === NoExistingOrganizationId
          }
        />
        {loadError && <span style={{ color: "#d32f2f" }}>{loadError}</span>}
      </div>
      <DataGrid
        dataSource={versions}
        keyExpr="rowId"
        height={500}
        showBorders={true}
        allowColumnResizing={true}
        columnAutoWidth={true}
        onSelectionChanged={({ selectedRowsData }) => {
          setSelectedVersion(selectedRowsData[0]);
        }}
        noDataText={
          organizationId === NoExistingOrganizationId
            ? "No organization is available."
            : "No snapshot versions found."
        }
      >
        <Selection mode="single" />
        <SearchPanel
          visible={true}
          highlightCaseSensitive={true}
          placeholder={pageProps.appUtil.getLocaleString("search")}
        />
        <ColumnChooser enabled={true} />
        <Sorting mode="multiple" />
        <FilterRow visible={true} />
        <Paging defaultPageSize={10} />
        <Column dataField="typeName" caption="Type" />
        <Column dataField="versionName" caption="Version Name" />
        <Column dataField="createdBy" caption="Created By" />
        <Column
          dataField="createdTime"
          caption="Created Time"
          dataType="datetime"
          format="yyyy-MM-dd HH:mm:ss"
        />
      </DataGrid>
      <LoadPanel
        shadingColor="rgba(0,0,0,0.4)"
        visible={isBusy}
        showIndicator={true}
        shading={true}
        showPane={true}
      />
      <Popup
        visible={isSaveDialogVisible}
        onHiding={() => setIsSaveDialogVisible(false)}
        dragEnabled={false}
        hideOnOutsideClick={true}
        showCloseButton={true}
        showTitle={true}
        title="Save Snapshot Version"
        width={400}
        height={230}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <span>Enter a name for this new snapshot version:</span>
          <TextBox
            value={newVersionName}
            onValueChanged={(e) => setNewVersionName(e.value ?? "")}
            placeholder="Snapshot version name"
            onEnterKey={saveCurrentRules}
          />
          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "flex-end",
            }}
          >
            <Button
              text="Cancel"
              stylingMode="outlined"
              onClick={() => setIsSaveDialogVisible(false)}
            />
            <Button
              text="Save"
              type="default"
              stylingMode="contained"
              onClick={saveCurrentRules}
            />
          </div>
        </div>
      </Popup>
    </MainCard>
  );
};
